import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Menu, Button, theme, Dropdown, Avatar, Space, Switch, ConfigProvider, Badge, Tooltip, Tag, Grid, Drawer, Modal, Form, Input, Descriptions, Tabs, message } from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    MenuOutlined,
    DashboardOutlined,
    ShopOutlined,
    TruckOutlined,
    GoldOutlined,
    LineChartOutlined,
    TeamOutlined,
    UserAddOutlined,
    SettingOutlined,
    AppstoreOutlined,
    UserOutlined,
    LogoutOutlined,
    StockOutlined,
    BankOutlined,
    ExperimentOutlined,
    AliwangwangOutlined,
    ApartmentOutlined,
    EnvironmentOutlined,
    ShoppingOutlined,
    TagsOutlined,
    FieldNumberOutlined,
    CarOutlined,
    FileTextOutlined,
    BookOutlined,
    PieChartOutlined,
    BellOutlined,
    CheckCircleOutlined,
    LockOutlined,
    FireOutlined,
    MoreOutlined,
    CloseOutlined,
    SunOutlined,
    MoonOutlined,
    KeyOutlined,
    EditOutlined,
    GiftOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import dayjs from 'dayjs';
import './AppLayout.css';

const { Header, Sider, Content } = Layout;
const brandLogo = '/coop.png';
const compactBrandLogo = '/logo192.png';
const brandTitle = 'Agri Inventory';
const brandSubtitle = 'Barbaza Multi-Purpose Cooperative';

const AppLayout = ({ children }) => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [collapsed, setCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { user, logout, refreshUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [profileOpen, setProfileOpen] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const masterDataKeys = useMemo(() => new Set([
        '/partners',
        '/master/branches',
        '/master/locations',
        '/master/items',
        '/master/categories',
        '/master/units',
        '/master/vehicles',
    ]), []);

    const palayOperationsKeys = useMemo(() => new Set([
        '/purchasing',
        '/drying',
        '/palay-hauling',
        '/milling',
    ]), []);

    const inventoryLogisticsKeys = useMemo(() => new Set([
        '/inventory',
        '/inventory/adjustments',
        '/hauling',
    ]), []);

    const salesPaymentKeys = useMemo(() => new Set([
        '/sales',
        '/rice-allowances',
        '/customers',
        '/payments',
    ]), []);

    const farmProductionKeys = useMemo(() => new Set([
        '/farms',
        '/livestock',
    ]), []);

    const accountingKeys = useMemo(() => new Set([
        '/accounting/journal-entries',
        '/accounting/coa',
        '/accounting/reports',
        '/accounting/subsidiary-ledgers',
        '/accounting/year-end-closing',
    ]), []);

    const [openKeys, setOpenKeys] = useState([]);

    // Global Dark Mode
    const [isDarkMode, setIsDarkMode] = useState(false);
    const isPrintingRef = useRef(false);
    const lastModeRef = useRef(false);
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);

    // Desktop: collapse only on first mount when small
    useEffect(() => {
        if (!isMobile) setCollapsed(false);
    }, [isMobile]);

    // Auto-close mobile drawer on route change
    useEffect(() => {
        if (mobileMenuOpen) setMobileMenuOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        const handleBeforePrint = () => {
            isPrintingRef.current = true;
            lastModeRef.current = isDarkMode;
        };

        const handleAfterPrint = () => {
            isPrintingRef.current = false;
            const dark = lastModeRef.current;
            setIsDarkMode(dark);
            if (dark) {
                document.body.classList.add('dark-mode');
            } else {
                document.body.classList.remove('dark-mode');
            }
        };

        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);

        const storedMode = localStorage.getItem('ui:dark-mode') === 'true';
        setIsDarkMode(storedMode);
        if (storedMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }

        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, [isDarkMode]);

    const toggleDarkMode = (checked) => {
        setIsDarkMode(checked);
        if (checked) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
        localStorage.setItem('ui:dark-mode', checked ? 'true' : 'false');
    };


    useEffect(() => {
        const pathIsIn = (keys) => Array.from(keys).some((key) =>
            location.pathname === key || location.pathname.startsWith(`${key}/`)
        );

        if (pathIsIn(palayOperationsKeys)) {
            const keys = ['palay-operations'];
            // Milling + Palay to Mill live in a nested 'milling-group' submenu;
            // open it too so the active child isn't hidden.
            if (location.pathname.startsWith('/milling') || location.pathname.startsWith('/palay-hauling')) {
                keys.push('milling-group');
            }
            setOpenKeys(keys);
        } else if (pathIsIn(inventoryLogisticsKeys)) {
            setOpenKeys(['inventory-logistics']);
        } else if (pathIsIn(salesPaymentKeys)) {
            setOpenKeys(['sales-payments']);
        } else if (pathIsIn(farmProductionKeys)) {
            setOpenKeys(['farm-production']);
        } else if (pathIsIn(masterDataKeys)) {
            setOpenKeys(['master-data']);
        } else if (pathIsIn(accountingKeys) || location.pathname.startsWith('/accounting')) {
            setOpenKeys(['accounting']);
        }
    }, [
        location.pathname,
        accountingKeys,
        farmProductionKeys,
        inventoryLogisticsKeys,
        masterDataKeys,
        palayOperationsKeys,
        salesPaymentKeys,
    ]);

    const isAdmin = user?.role === 'ADMIN';
    const notificationsCount = notifications.length;

    const fetchNotifications = async () => {
        if (!isAdmin) return;
        setNotificationsLoading(true);
        try {
            const startDate = dayjs().startOf('month');
            const endDate = dayjs().endOf('month');
            const rangeParams = {
                start_date: startDate.format('YYYY-MM-DD'),
                end_date: endDate.format('YYYY-MM-DD'),
                as_of_date: endDate.format('YYYY-MM-DD'),
            };

            const [statsRes, millingRes, incomeRes, settingsRes] = await Promise.allSettled([
                client.get('/dashboard/stats'),
                client.get('/transactions/milling', { params: rangeParams }),
                client.get('/transactions/reports/income-statement', { params: rangeParams }),
                client.get('/admin/notification-settings'),
            ]);

            const alerts = [];
            const statsData = statsRes.status === 'fulfilled' ? statsRes.value.data : {};
            const lowStockItems = statsData.low_stock || [];
            if (lowStockItems.length > 0) {
                const topItems = lowStockItems.slice(0, 3).map(item => item.name).join(', ');
                const extraCount = lowStockItems.length > 3 ? ` +${lowStockItems.length - 3} more` : '';
                alerts.push({
                    key: 'low-stock',
                    severity: 'critical',
                    title: 'Low stock alert',
                    description: `${lowStockItems.length} items below reorder level${topItems ? `: ${topItems}${extraCount}` : ''}.`,
                    link: '/inventory',
                });
            }

            const settings = settingsRes.status === 'fulfilled' ? settingsRes.value.data : {};
            const LOW_RECOVERY_WARNING = Number(settings.low_recovery_warning) || 60;
            const LOW_RECOVERY_CRITICAL = Number(settings.low_recovery_critical) || 55;
            const millingOrders = millingRes.status === 'fulfilled' && Array.isArray(millingRes.value.data)
                ? millingRes.value.data
                : [];
            const lowRecoveryWarning = millingOrders.filter(order =>
                order.recovery_rate !== null && Number(order.recovery_rate) < LOW_RECOVERY_WARNING
            );
            const lowRecoveryCritical = millingOrders.filter(order =>
                order.recovery_rate !== null && Number(order.recovery_rate) < LOW_RECOVERY_CRITICAL
            );
            if (lowRecoveryWarning.length > 0) {
                const severity = lowRecoveryCritical.length > 0 ? 'critical' : 'warning';
                const topBatches = lowRecoveryWarning.slice(0, 3).map(order => order.order_number).join(', ');
                const extraCount = lowRecoveryWarning.length > 3 ? ` +${lowRecoveryWarning.length - 3} more` : '';
                alerts.push({
                    key: 'low-recovery',
                    severity,
                    title: 'Low recovery rate',
                    description: `${lowRecoveryWarning.length} milling batches below ${LOW_RECOVERY_WARNING}% recovery${topBatches ? `: ${topBatches}${extraCount}` : ''}.`,
                    link: '/milling',
                });
            }

            const netIncome = Number(incomeRes.status === 'fulfilled' ? incomeRes.value.data?.net_income : NaN);
            const monthlySales = Number(statsData?.sales?.monthly);
            const netLossFloor = Number(settings.net_loss_floor) || 5000;
            const netLossPercent = Number(settings.net_loss_percent) || 2;
            const netLossPercentFloor = !Number.isNaN(monthlySales) ? monthlySales * (netLossPercent / 100) : 0;
            const netLossThreshold = Math.max(netLossFloor, netLossPercentFloor);
            if (!Number.isNaN(netIncome) && netIncome < 0 && Math.abs(netIncome) >= netLossThreshold) {
                alerts.push({
                    key: 'net-loss',
                    severity: 'critical',
                    title: 'Net loss detected',
                    description: `Current month net income is negative (${netIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).`,
                    link: '/accounting/reports',
                });
            }

            setNotifications(alerts);
        } catch (error) {
            console.error('Failed to fetch notifications', error);
        } finally {
            setNotificationsLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const notificationColor = (severity) => {
        if (severity === 'critical') return 'red';
        if (severity === 'warning') return 'orange';
        return 'blue';
    };

    const openProfile = () => {
        profileForm.setFieldsValue({
            name: user?.name,
            email: user?.email,
        });
        passwordForm.resetFields();
        setProfileOpen(true);
    };

    const handleProfileSave = async (values) => {
        setProfileSaving(true);
        try {
            await client.put('/auth/profile', values);
            await refreshUser();
            message.success('Profile updated');
        } catch (error) {
            const errors = error.response?.data?.errors ? Object.values(error.response.data.errors).flat().join(' ') : '';
            message.error(error.response?.data?.message || errors || 'Failed to update profile');
        } finally {
            setProfileSaving(false);
        }
    };

    const handlePasswordSave = async (values) => {
        setPasswordSaving(true);
        try {
            await client.put('/auth/password', values);
            passwordForm.resetFields();
            message.success('Password changed');
        } catch (error) {
            const errors = error.response?.data?.errors ? Object.values(error.response.data.errors).flat().join(' ') : '';
            message.error(error.response?.data?.message || errors || 'Failed to change password');
        } finally {
            setPasswordSaving(false);
        }
    };

    const notificationsMenu = (
        <div className="app-notifications-menu">
            <div className="app-notifications-header">
                <span>Alerts</span>
                <Button
                    size="small"
                    type="link"
                    onClick={fetchNotifications}
                    loading={notificationsLoading}
                    className="app-notifications-refresh"
                >
                    Refresh
                </Button>
            </div>
            <div className="app-notifications-body">
                {notificationsLoading && (
                    <div className="app-notifications-empty">Loading alerts...</div>
                )}
                {!notificationsLoading && notifications.length === 0 && (
                    <div className="app-notifications-empty">No alerts right now.</div>
                )}
                {!notificationsLoading && notifications.map((notice) => (
                    <button
                        key={notice.key}
                        type="button"
                        className="app-notifications-item"
                        onClick={() => notice.link && navigate(notice.link)}
                        disabled={!notice.link}
                    >
                        <div className="app-notifications-title">
                            <span>{notice.title}</span>
                            <Tag color={notificationColor(notice.severity)} className="app-notifications-tag">
                                {notice.severity}
                            </Tag>
                        </div>
                        <div className="app-notifications-desc">{notice.description}</div>
                    </button>
                ))}
            </div>
        </div>
    );

    const menuItems = [
        { key: '/', icon: <DashboardOutlined />, label: 'Dashboard', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
        {
            key: 'palay-operations',
            icon: <GoldOutlined />,
            label: 'Palay Operations',
            roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'],
            children: [
                { key: '/purchasing', icon: <ShopOutlined />, label: 'Purchasing', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                { key: '/drying', icon: <FireOutlined />, label: 'Drying', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                {
                    key: 'milling-group',
                    icon: <GoldOutlined />,
                    label: 'Milling',
                    roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'],
                    children: [
                        { key: '/palay-hauling', icon: <TruckOutlined />, label: 'Palay to Mill', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                        { key: '/milling', icon: <GoldOutlined />, label: 'Milling Orders', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                    ],
                },
            ],
        },
        {
            key: 'inventory-logistics',
            icon: <StockOutlined />,
            label: 'Inventory & Logistics',
            roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'],
            children: [
                { key: '/inventory', icon: <StockOutlined />, label: 'Stock Card', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                { key: '/inventory/adjustments', icon: <EditOutlined />, label: 'Adjustments', roles: ['ADMIN'] },
                { key: '/hauling', icon: <TruckOutlined />, label: 'Stock Transfer', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
            ],
        },
        {
            key: 'sales-payments',
            icon: <LineChartOutlined />,
            label: 'Sales & Payments',
            roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'],
            children: [
                { key: '/sales', icon: <LineChartOutlined />, label: 'Sales', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                { key: '/rice-allowances', icon: <GiftOutlined />, label: 'Rice Allowance', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                { key: '/customers', icon: <UserAddOutlined />, label: 'Customers', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                { key: '/payments', icon: <BankOutlined />, label: 'Payments', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
            ],
        },
        {
            key: 'farm-production',
            icon: <ExperimentOutlined />,
            label: 'Farm Production',
            roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'],
            children: [
                { key: '/farms', icon: <ExperimentOutlined />, label: 'Farms & Cycles', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
                { key: '/livestock', icon: <AliwangwangOutlined />, label: 'Livestock', roles: ['ADMIN', 'MANAGER', 'STAFF', 'VIEWER'] },
            ],
        },
        {
            key: 'master-data',
            icon: <AppstoreOutlined />,
            label: 'Master Data',
            roles: ['ADMIN', 'MANAGER'],
            children: [
                { key: '/partners', icon: <TeamOutlined />, label: 'Partners', roles: ['ADMIN', 'MANAGER'] },
                { key: '/master/branches', icon: <ApartmentOutlined />, label: 'Branches', roles: ['ADMIN', 'MANAGER'] },
                { key: '/master/locations', icon: <EnvironmentOutlined />, label: 'Locations', roles: ['ADMIN', 'MANAGER'] },
                { key: '/master/items', icon: <ShoppingOutlined />, label: 'Items', roles: ['ADMIN', 'MANAGER'] },
                { key: '/master/categories', icon: <TagsOutlined />, label: 'Item Categories', roles: ['ADMIN', 'MANAGER'] },
                { key: '/master/units', icon: <FieldNumberOutlined />, label: 'Units', roles: ['ADMIN', 'MANAGER'] },
                { key: '/master/vehicles', icon: <CarOutlined />, label: 'Vehicles', roles: ['ADMIN', 'MANAGER'] },
            ],
        },
        {
            key: 'accounting',
            icon: <BankOutlined />,
            label: 'Accounting',
            roles: ['ADMIN', 'MANAGER'],
            children: [
                { key: '/accounting/journal-entries', icon: <FileTextOutlined />, label: 'Journal Entries', roles: ['ADMIN', 'MANAGER'] },
                { key: '/accounting/coa', icon: <BookOutlined />, label: 'Chart of Accounts', roles: ['ADMIN', 'MANAGER'] },
                { key: '/accounting/reports', icon: <PieChartOutlined />, label: 'Financial Reports', roles: ['ADMIN', 'MANAGER'] },
                { key: '/accounting/subsidiary-ledgers', icon: <TeamOutlined />, label: 'AR/AP Ledgers', roles: ['ADMIN', 'MANAGER'] },
                { key: '/accounting/year-end-closing', icon: <LockOutlined />, label: 'Year-End Closing', roles: ['ADMIN'] },
            ]
        },
        { key: '/approvals', icon: <CheckCircleOutlined />, label: 'Approvals', roles: ['ADMIN', 'MANAGER'] },
        { key: '/admin', icon: <SettingOutlined />, label: 'Admin', roles: ['ADMIN'] },
    ];

    const filterMenuItems = (items) => {
        return items
            .filter(item => !item.roles || item.roles.includes(user?.role))
            .map(item => {
                if (item.children) {
                    return { ...item, children: filterMenuItems(item.children) };
                }
                return item;
            })
            .filter(item => !item.children || item.children.length > 0);
    };

    const filteredItems = filterMenuItems(menuItems);
    const routeMenuKeys = [
        '/',
        '/purchasing',
        '/drying',
        '/palay-hauling',
        '/milling',
        '/inventory/adjustments',
        '/inventory',
        '/hauling',
        '/rice-allowances',
        '/sales',
        '/customers',
        '/payments',
        '/farms',
        '/livestock',
        '/partners',
        '/master/branches',
        '/master/locations',
        '/master/items',
        '/master/categories',
        '/master/units',
        '/master/vehicles',
        '/accounting/journal-entries',
        '/accounting/coa',
        '/accounting/reports',
        '/accounting/subsidiary-ledgers',
        '/accounting/year-end-closing',
        '/approvals',
        '/admin',
    ];
    const selectedMenuKey = routeMenuKeys.find((key) =>
        key === '/'
            ? location.pathname === key
            : location.pathname === key || location.pathname.startsWith(`${key}/`)
    ) || location.pathname;

    const userMenu = {
        items: [
            { key: 'profile', icon: <UserOutlined />, label: 'Profile', onClick: openProfile },
            { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: logout },
        ],
    };

    // Mobile "More" menu — bundles notifications, dark-mode toggle, branch info
    const mobileMoreMenu = {
        items: [
            ...(isAdmin ? [{
                key: 'notifications',
                icon: <BellOutlined />,
                label: `Alerts (${notificationsCount})`,
                onClick: () => {
                    // Show notifications inline as a navigation; reuse alert links if only one
                    if (notifications.length > 0 && notifications[0].link) {
                        navigate(notifications[0].link);
                    }
                },
            }] : []),
            {
                key: 'profile',
                icon: <UserOutlined />,
                label: 'Profile',
                onClick: openProfile,
            },
            {
                key: 'darkmode',
                icon: isDarkMode ? <SunOutlined /> : <MoonOutlined />,
                label: isDarkMode ? 'Switch to Light' : 'Switch to Dark',
                onClick: () => toggleDarkMode(!isDarkMode),
            },
            {
                key: 'branch',
                icon: <ApartmentOutlined />,
                label: user?.branch?.name || 'Main Office',
                disabled: true,
            },
        ],
    };

    const headerBg = isDarkMode ? '#141414' : colorBgContainer;

    // Mobile menu drawer header (with close button)
    const mobileMenuTitle = (
        <div className="mobile-menu-handle-wrap">
            <div className="mobile-menu-handle" />
        </div>
    );

    return (
        <ConfigProvider theme={{ algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
            <Layout className="app-layout" style={{ minHeight: '100vh' }}>
                {/* Desktop Sider — hidden on mobile */}
                {!isMobile && (
                    <Sider
                        trigger={null}
                        collapsible
                        collapsed={collapsed}
                        theme="dark"
                        breakpoint="md"
                        collapsedWidth={80}
                        width={220}
                        className="app-sider"
                    >
                        <div className="app-sider-logo">
                            {collapsed
                                ? <img src={compactBrandLogo} alt={brandTitle} className="app-brand-icon" />
                                : (
                                    <>
                                        <img src={brandLogo} alt={brandTitle} className="app-brand-logo" />
                                        <span>{brandTitle}</span>
                                    </>
                                )
                            }
                        </div>
                        <Menu
                            theme="dark"
                            mode="inline"
                            selectedKeys={[selectedMenuKey]}
                            openKeys={openKeys}
                            onOpenChange={(keys) => setOpenKeys(keys)}
                            items={filteredItems}
                            onClick={({ key }) => navigate(key)}
                        />
                    </Sider>
                )}

                {/* Mobile Bottom Sheet Drawer */}
                {isMobile && (
                    <Drawer
                        placement="bottom"
                        open={mobileMenuOpen}
                        onClose={() => setMobileMenuOpen(false)}
                        height="85vh"
                        closable={false}
                        title={mobileMenuTitle}
                        className="mobile-menu-drawer"
                        styles={{
                            header: { padding: '8px 0 0 0', border: 'none', background: isDarkMode ? '#0f172a' : '#1e293b' },
                            body:   { padding: 0, background: isDarkMode ? '#0f172a' : '#1e293b' },
                            content: { borderRadius: '20px 20px 0 0', overflow: 'hidden' },
                        }}
                    >
                        <div className="mobile-menu-header">
                            <span className="mobile-menu-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <img src={brandLogo} alt="" className="app-brand-logo app-brand-logo-mobile-menu" />
                                {brandTitle}
                            </span>
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={() => setMobileMenuOpen(false)}
                                aria-label="Close menu"
                                className="mobile-menu-close"
                            />
                        </div>
                        <div className="mobile-menu-user">
                            <Avatar icon={<UserOutlined />} size={36} />
                            <div className="mobile-menu-user-info">
                                <div className="mobile-menu-user-name">{user?.name}</div>
                                <div className="mobile-menu-user-role">{user?.role} · {user?.branch?.name || 'Main Office'}</div>
                            </div>
                        </div>
                        <Menu
                            theme="dark"
                            mode="inline"
                            selectedKeys={[selectedMenuKey]}
                            openKeys={openKeys}
                            onOpenChange={(keys) => setOpenKeys(keys)}
                            items={filteredItems}
                            onClick={({ key }) => {
                                navigate(key);
                                setMobileMenuOpen(false);
                            }}
                            style={{ borderRight: 'none', flex: 1, overflowY: 'auto' }}
                        />
                        <div className="mobile-menu-footer">
                            <Button
                                block
                                danger
                                icon={<LogoutOutlined />}
                                onClick={logout}
                                style={{ height: 44, borderRadius: 8 }}
                            >
                                Logout
                            </Button>
                        </div>
                    </Drawer>
                )}

                <Layout>
                    <Header className="app-header" style={{ background: headerBg }}>
                        {/* Trigger button: hamburger on mobile, fold/unfold on desktop */}
                        <Button
                            type="text"
                            icon={isMobile ? <MenuOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
                            onClick={() => {
                                if (isMobile) setMobileMenuOpen(true);
                                else setCollapsed(!collapsed);
                            }}
                            className="app-sider-trigger"
                            aria-label={isMobile ? 'Open menu' : (collapsed ? 'Expand sidebar' : 'Collapse sidebar')}
                        />

                        {/* Mobile-only app title in header */}
                        {isMobile && (
                            <span className="app-header-title">
                                <img src={brandLogo} alt="" className="app-brand-logo app-brand-logo-header" />
                                <span className="app-header-title-text">
                                    <span>{brandTitle}</span>
                                    <small>{brandSubtitle}</small>
                                </span>
                            </span>
                        )}

                        <div className="app-header-right">
                            {/* Desktop: full controls */}
                            {!isMobile && (
                                <>
                                    {isAdmin && (
                                        <Dropdown trigger={['click']} dropdownRender={() => notificationsMenu}>
                                            <Tooltip title="Notifications">
                                                <Button
                                                    type="text"
                                                    className="app-notifications"
                                                    icon={(
                                                        <Badge count={notificationsCount} size="small" overflowCount={99}>
                                                            <BellOutlined />
                                                        </Badge>
                                                    )}
                                                />
                                            </Tooltip>
                                        </Dropdown>
                                    )}
                                    <Switch
                                        checked={isDarkMode}
                                        onChange={toggleDarkMode}
                                        checkedChildren="Dark"
                                        unCheckedChildren="Light"
                                        style={{ marginRight: 16 }}
                                    />
                                    <span className="app-header-branch">{user?.branch?.name || 'Main Office'}</span>
                                    <Dropdown menu={userMenu}>
                                        <Space className="app-user" style={{ cursor: 'pointer' }}>
                                            <Avatar icon={<UserOutlined />} />
                                            <span className="app-user-name">{user?.name}</span>
                                        </Space>
                                    </Dropdown>
                                </>
                            )}

                            {/* Mobile: condensed controls */}
                            {isMobile && (
                                <>
                                    {isAdmin && notificationsCount > 0 && (
                                        <Dropdown trigger={['click']} dropdownRender={() => notificationsMenu}>
                                            <Button
                                                type="text"
                                                className="app-notifications"
                                                icon={(
                                                    <Badge count={notificationsCount} size="small" overflowCount={99}>
                                                        <BellOutlined style={{ fontSize: 18 }} />
                                                    </Badge>
                                                )}
                                                aria-label="Notifications"
                                            />
                                        </Dropdown>
                                    )}
                                    <Dropdown menu={mobileMoreMenu} trigger={['click']} placement="bottomRight">
                                        <Button
                                            type="text"
                                            icon={<MoreOutlined style={{ fontSize: 20 }} />}
                                            aria-label="More options"
                                        />
                                    </Dropdown>
                                </>
                            )}
                        </div>
                    </Header>
                    <Content
                        className="app-content"
                        style={{
                            minHeight: 280,
                            background: headerBg,
                            borderRadius: borderRadiusLG,
                        }}
                    >
                        {children}
                    </Content>
                </Layout>
            </Layout>
            <Modal
                title="Profile"
                open={profileOpen}
                onCancel={() => setProfileOpen(false)}
                footer={null}
                width={560}
                destroyOnClose
            >
                <Tabs
                    items={[
                        {
                            key: 'details',
                            label: 'Details',
                            children: (
                                <Space direction="vertical" size={18} style={{ width: '100%' }}>
                                    <Descriptions bordered size="small" column={1}>
                                        <Descriptions.Item label="Role">{user?.role || '-'}</Descriptions.Item>
                                        <Descriptions.Item label="Branch">{user?.branch?.name || 'Main Office'}</Descriptions.Item>
                                    </Descriptions>
                                    <Form form={profileForm} layout="vertical" onFinish={handleProfileSave}>
                                        <Form.Item
                                            name="name"
                                            label="Name"
                                            rules={[{ required: true, message: 'Enter your name' }]}
                                        >
                                            <Input prefix={<UserOutlined />} />
                                        </Form.Item>
                                        <Form.Item
                                            name="email"
                                            label="Email"
                                            rules={[
                                                { required: true, message: 'Enter your email' },
                                                { type: 'email', message: 'Enter a valid email' },
                                            ]}
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Button type="primary" htmlType="submit" loading={profileSaving}>
                                            Save Profile
                                        </Button>
                                    </Form>
                                </Space>
                            ),
                        },
                        {
                            key: 'password',
                            label: 'Password',
                            children: (
                                <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSave}>
                                    <Form.Item
                                        name="current_password"
                                        label="Current Password"
                                        rules={[{ required: true, message: 'Enter your current password' }]}
                                    >
                                        <Input.Password prefix={<KeyOutlined />} />
                                    </Form.Item>
                                    <Form.Item
                                        name="password"
                                        label="New Password"
                                        rules={[{ required: true, min: 8, message: 'Password must be at least 8 characters' }]}
                                    >
                                        <Input.Password prefix={<LockOutlined />} />
                                    </Form.Item>
                                    <Form.Item
                                        name="password_confirmation"
                                        label="Confirm New Password"
                                        dependencies={['password']}
                                        rules={[
                                            { required: true, message: 'Confirm your new password' },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('password') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Passwords do not match'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password prefix={<LockOutlined />} />
                                    </Form.Item>
                                    <Button type="primary" htmlType="submit" loading={passwordSaving}>
                                        Change Password
                                    </Button>
                                </Form>
                            ),
                        },
                    ]}
                />
            </Modal>
        </ConfigProvider>
    );
};

export default AppLayout;
